import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  useTheme,
  TableRow,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  Grid,
  Paper,
} from "@mui/material";
import { LoadingButton as Button } from "@mui/lab";
import TablePagination from "@/components/Table/TablePagination";
import { IAsset, ICurrency, IOrder, IVWPost } from "@/interfaces";
import { autorun } from "mobx";
import { ManagePost } from "@/components/Modal";
import Store from "@/store";
import { OrderStore } from "@/store/orders";
import { TradingStore } from "@/store/Trading";
import CommonStore, { UI_ACTION_STATE } from "@/store/common";
import { UserStore } from "@/store/user";

import { inject, observer } from "mobx-react";
import { ORDER_STATUS, POST_STATUS } from "@/@types/EnumType";
import MarketContractStore from "@/store/contract";

import { formatedBigNumber } from "@/utility";
import { enqueueSnackbar } from "notistack";
import numeral from "numeral";

const MyPostsPage = inject(
  Store.type.orderStore,
  Store.type.userStore,
  Store.type.commonStore,
  Store.type.tradingStore,
  Store.type.contractStore,
)(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  observer((props: any) => {
    const userStore = props[Store.type.userStore] as UserStore;
    const commonStore = props[Store.type.commonStore] as CommonStore;
    const tradingStore = props[Store.type.tradingStore] as TradingStore;
    const orderStore = props[Store.type.orderStore] as OrderStore;
    const contractStore = props[
      Store.type.contractStore
    ] as MarketContractStore;

    const theme = useTheme();
    const navigate = useNavigate();

    const [posts, setPosts] = useState<IVWPost[]>([]);
    const [selectedPost, setSelectedPost] = useState<IVWPost | null>(null);
    const [postsOrderList, setPostsOrderList] = useState<IOrder[]>([]);
    const [assetList, setAssetList] = useState<IAsset[]>([]);
    const [currencyList, setCurrencyList] = useState<ICurrency[]>([]);

    useEffect(() => {
      let unsubscribeMyPost: () => void;
      const disposeAutorun = autorun(async () => {
        const userId = userStore.userId;
        if (userId) {
          commonStore.fetchAssets().catch((error) => {
            console.error("fetchAssets failed: " + error);
          });
          commonStore.fetchCurrencies().catch((error) => {
            console.error("Error currencies:", error);
          });
          if (!unsubscribeMyPost) {
            unsubscribeMyPost = await tradingStore.subscribeToMyPosts(userId);
            orderStore.fetchMyPostsOrders(userId).catch((error) => {
              console.error("Error fetch my posts orders:", error);
            });
          }
          userStore.fetchMyPayments(userId).catch((error) => {
            console.error("fetchPaymentMethods failed: " + error);
          });
        }
      });

      return () => {
        disposeAutorun();
        if (unsubscribeMyPost) {
          unsubscribeMyPost();
        }
      };
    }, [userStore]);

    useEffect(() => {
      const disposeAutorun = autorun(async () => {
        const assets = commonStore.assets;
        const MyPosts = tradingStore.myPosts;
        const MyPostOrders = orderStore.getMyPostsOrders;
        const currencies = commonStore.currencies;
        if (MyPosts && assets && MyPostOrders && currencies) {
          setPosts(MyPosts);
          setAssetList(assets);
          setPostsOrderList(MyPostOrders);
          setCurrencyList(currencies);
        }
      });

      return () => disposeAutorun();
    }, [tradingStore, commonStore, orderStore]);
    const handleRemovePost = async (request: {
      user_id: string;
      post_id: string;
    }) => {
      const { user_id, post_id } = request;

      const post = tradingStore.myPosts.find(
        (post) => post.post_id === post_id,
      );
      commonStore.setActionState(UI_ACTION_STATE.WALLET_CONFIRMATION_PENDING);
      await contractStore.cancelPost(post?.contract_post_id || "", (result) => {
        commonStore.setActionState(result);
      });
      commonStore.setActionState(UI_ACTION_STATE.NONE);
      await tradingStore.removePost({ user_id, post_id });

      return true;
    };
    const handleOpenPost = async (request: { post_id: string }) => {
      const { post_id } = request;
      commonStore.setActionState(UI_ACTION_STATE.WALLET_CONFIRMATION_PENDING);
      try {
        await contractStore.activatePost(post_id, (result) => {
          commonStore.setActionState(result);
        });
        enqueueSnackbar("Set post status to open successfully", {
          variant: "success",
        });
      } catch (error) {
        enqueueSnackbar("Failed to open post", { variant: "error" });
      }
      commonStore.setActionState(UI_ACTION_STATE.NONE);
      return true;
    };
    const handleClosePost = async (request: { post_id: string }) => {
      const { post_id } = request;
      commonStore.setActionState(UI_ACTION_STATE.WALLET_CONFIRMATION_PENDING);
      try {
        await contractStore.deactivatePost(post_id, (result) => {
          commonStore.setActionState(result);
        });
        enqueueSnackbar("Set post status to closed successfully", {
          variant: "success",
        });
      } catch (error) {
        enqueueSnackbar("Failed to close post", { variant: "error" });
      }
      commonStore.setActionState(UI_ACTION_STATE.NONE);
      return true;
    };

    const isAnyOrderOpen = (): boolean => {
      if (!selectedPost) {
        return false;
      }
      const postOrders = postsOrderList.filter(
        (order) => order.post_id === selectedPost.post_id,
      );
      return postOrders.some((order) => order.status === ORDER_STATUS.OPEN);
    };

    useEffect(() => {
      if (selectedPost) {
        postsOrderList.find;
      }
    }, [postsOrderList, selectedPost]);

    const [page, setPage] = useState(1);
    const rowsPerPage = 12;
    const handleChangePage = (newPage: number) => {
      setPage(newPage);
    };
    const StatusText = (status: POST_STATUS) => {
      switch (status) {
        case POST_STATUS.ACTIVE:
          return "OPEN";
        case POST_STATUS.INACTIVE:
          return "CLOSED";
        case POST_STATUS.CANCELLED:
          return "CANCELLED";
        default:
          return "";
      }
    };

    return (
      <>
        <Paper
          sx={{
            m: "82.5px 78px 50.5px 56px",
            p: "68px 54px 50px 64px",
            minHeight: `calc((100vh - ${theme.navbarHeight} - ${theme.footerHeight} - 82.5px - 50.5px))`,
          }}
        >
          <Grid
            container
            spacing={1}
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
            alignContent="stretch"
            wrap="wrap"
            mb="62.25px"
          >
            <Grid item sm={4}></Grid>
            <Grid item sm={4}>
              <Typography variant="h3" textAlign="center">
                My Posts
              </Typography>
            </Grid>
            <Grid item sm={4}>
              <Button
                onClick={() => navigate("/trade/create-post")}
                variant="outlined"
                color="primary"
                size="large"
                sx={{ float: "right" }}
              >
                Create post
              </Button>
            </Grid>
          </Grid>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell style={{ width: "12.28%" }}>Side</TableCell>
                <TableCell>Asset</TableCell>
                <TableCell>Orders</TableCell>
                <TableCell style={{ width: "12.28%" }}>Available</TableCell>
                <TableCell style={{ width: "14.28%" }}>Price</TableCell>
                <TableCell style={{ width: "18.28%" }}>Limits</TableCell>
                <TableCell style={{ width: "14.28%" }}>
                  Payment methods
                </TableCell>

                <TableCell style={{ width: "14.28%" }}>Created At</TableCell>
                <TableCell style={{ width: "12.28%" }}>Status</TableCell>
                <TableCell style={{ width: "14.28%" }}>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {posts
                .slice(
                  (page - 1) * rowsPerPage,
                  (page - 1) * rowsPerPage + rowsPerPage,
                )
                .map((row: IVWPost) => (
                  <TableRow
                    key={row.post_id}
                    sx={{
                      borderRadius: theme.borderRadius.main,
                    }}
                  >
                    <TableCell
                      sx={{
                        color:
                          row.post_type === "SELL"
                            ? `${theme.colors.red} !important`
                            : `${theme.colors.green} !important`,
                      }}
                    >
                      {row.post_type}
                    </TableCell>
                    <TableCell>{row.asset_name}</TableCell>
                    <TableCell>{row.orderavariable}</TableCell>
                    <TableCell>
                      {numeral(formatedBigNumber(row.token_amount)).format(
                        "0,0.000",
                      )}{" "}
                      {row.asset_name}
                    </TableCell>

                    <TableCell>
                      {formatedBigNumber(row.price)} {row.currency}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body4">
                        {`${
                          row.minOrder &&
                          numeral(formatedBigNumber(row.minOrder)).format(
                            "0,0.000",
                          )
                        } - ${
                          row.maxOrder &&
                          numeral(formatedBigNumber(row.maxOrder)).format(
                            "0,0.000",
                          )
                        } ${row.currency}`}
                      </Typography>
                    </TableCell>
                    <TableCell>{row.payment_name}</TableCell>
                    <TableCell>
                      <Typography variant="body4">
                        {new Date(row.created_at).toLocaleDateString()}{" "}
                        {new Date(row.created_at).toLocaleTimeString()}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        color:
                          row.status === POST_STATUS.INACTIVE ||
                          row.status === POST_STATUS.CANCELLED
                            ? `${theme.colors.red} !important`
                            : `${theme.colors.green} !important`,
                      }}
                    >
                      {StatusText(row.status)}
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="table"
                        color="primary"
                        size="small"
                        onClick={() => setSelectedPost(row)}
                      >
                        Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
          {posts.length > rowsPerPage && (
            <TablePagination
              page={page}
              count={posts.length}
              pageLimit={rowsPerPage}
              onChange={handleChangePage}
            />
          )}
          {posts.length === 0 && (
            <>
              <Typography
                variant="h3"
                color={theme.colors.noFound}
                mt="82.24px"
                textAlign="center"
              >
                You haven&apos;t created any posts yet.
              </Typography>
              <Box width="fit-content" mt="59px" mb="69px" mx="auto">
                <Button
                  variant="outlined"
                  color="primary"
                  size="medium"
                  onClick={() => {
                    navigate("/trade/create-post");
                  }}
                >
                  Create a new post
                </Button>
              </Box>
            </>
          )}
        </Paper>
        {selectedPost && (
          <ManagePost
            setSelectedPost={setSelectedPost}
            user_id={userStore.userId}
            updatePost={tradingStore.updatePost}
            removePost={handleRemovePost}
            initialPostData={selectedPost}
            openPost={handleOpenPost}
            closePost={handleClosePost}
            assetList={assetList}
            currencyList={currencyList}
            isAnyOrderOpen={isAnyOrderOpen()}
          />
        )}
      </>
    );
  }),
);

export default MyPostsPage;
